import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { getDeviceVendor, createDeviceVendor, updateDeviceVendor } from "./deviceService";
import { deviceVendorForm } from "../configs/Forms/AccountsForms";
import VendorFunctionsTable from "./VendorFunctionsTable";

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

function getAuthToken() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return localStorage.getItem("token");
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.token || parsed?.accessToken || null;
  } catch (e) {
    console.error("Failed to parse localStorage user:", e);
    return localStorage.getItem("token");
  }
}

const DeviceVendorAddEdit = () => {
  const { device_vendor_uuid } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(device_vendor_uuid);

  const [vendor, setVendor] = useState({});
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState("");

  // Fetch vendor if editing
  useEffect(() => {
    if (!editing) return;
    const fetchVendor = async () => {
      try {
        setLoading(true);
        const data = await getDeviceVendor(device_vendor_uuid);
        if (data.error) {
          setError(data.error);
          return;
        }
        setVendor(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchVendor();
  }, [device_vendor_uuid, editing]);

  const handleChange = (field, value) => setVendor(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setError("");
    const missing = [];
    const user = JSON.parse(localStorage.getItem("user"));
    const user_uuid = user?.user_uuid || null;

    deviceVendorForm.required.forEach(field => {
      if (!vendor[field]) missing.push(field);
    });

    if (missing.length) {
      setError(`Required fields missing: ${missing.join(", ")}`);
      return;
    }

    try {
      if (editing) await updateDeviceVendor(device_vendor_uuid, vendor, user_uuid);
      else await createDeviceVendor(vendor, user_uuid);

      navigate("/admin/hucs/deviceVendors");
    } catch (err) {
      setError(err.message || "Failed to save vendor");
    }
  };

  const renderField = (field, value, onChange) => {
    const type = deviceVendorForm.types[field];
    const options = deviceVendorForm.options?.[field] || [];
    const defaultValue = deviceVendorForm.defaults?.[field];
    const effectiveValue = value ?? defaultValue ?? "";
    const label =
      deviceVendorForm.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : `Enter ${label.toLowerCase()}`;

    // BOOLEAN
    if (type === "boolean") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Input>
      );
    }

    // SELECT (future-proof, even if you don’t use it yet)
    if (type === "select") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">
            Select {deviceVendorForm.labels[field] || field}
          </option>

          {options.map((opt) =>
            typeof opt === "object" ? (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ) : (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )
          )}
        </Input>
      );
    }

    // DEFAULT (text / number)
    return (
      <Input
        type="text"
        value={effectiveValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading vendor...</p>
      </div>
    );

  return (
    <>
      <SimpleHeader name={`${editing ? "Edit" : "Add"} Device Vendor`} parentName="Device Vendors" />
      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <h4 className="mb-0">{editing ? "EDIT VENDOR" : "ADD VENDOR"}</h4>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            <Form>
              {Object.entries(deviceVendorForm.sections).map(([sectionName, fields]) => (
                <div key={sectionName}>
                  <h5 className="section-title mb-3 mt-4" style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                    {sectionName}
                  </h5>
                  <Row className="mb-2">
                    {fields.map((field, i) => (
                      <Col md={4} key={i}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {deviceVendorForm.labels[field] || field}
                            {deviceVendorForm.required.includes(field) && <span style={{ color: "red" }}> *</span>}
                          </Form.Label>
                          {renderField(
                            field,
                            vendor[field] ?? "",
                            value => handleChange(field, value)
                          )}
                        </Form.Group>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={() => navigate("/admin/hucs/deviceVendors")}>Cancel</Button>
                <Button variant="success" onClick={handleSave}>{"Save"}</Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {/* Vendor Functions Table */}
        {editing && vendor.device_vendor_uuid && (
          <VendorFunctionsTable vendorUuid={vendor.device_vendor_uuid} />
        )}
      </Container>
    </>
  );
};

export default DeviceVendorAddEdit;
